import { useCallback, useEffect, useState } from 'react';
import { getReport, listAllAttempts, listExercises, listGames, listPuzzleAttempts } from '../lib/storage/db';
import type { CoachingReport, Exercise, ExerciseAttempt, PuzzleAttempt, StoredGame } from '../types';

export interface LibraryState {
  games: StoredGame[];
  exercises: Exercise[];
  attempts: ExerciseAttempt[];
  puzzleAttempts: PuzzleAttempt[];
  reports: Record<string, CoachingReport>;
  loading: boolean;
  storageError: string | null;
}

/**
 * Charge l'état persistant (IndexedDB) utilisé par plusieurs onglets
 * (Mes exercices, Ma progression). "Ma partie" gère sa propre partie
 * sélectionnée séparément mais déclenche `refresh()` après sauvegarde.
 */
export function useLibrary() {
  const [state, setState] = useState<LibraryState>({
    games: [],
    exercises: [],
    attempts: [],
    puzzleAttempts: [],
    reports: {},
    loading: true,
    storageError: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const games = await listGames();
      const exercises = await listExercises();
      const attempts = await listAllAttempts();
      const puzzleAttempts = await listPuzzleAttempts();
      const reportEntries = await Promise.all(games.map((g) => getReport(g.game.id)));
      const reports: Record<string, CoachingReport> = {};
      games.forEach((g, i) => {
        const r = reportEntries[i];
        if (r) reports[g.game.id] = r;
      });
      setState({ games, exercises, attempts, puzzleAttempts, reports, loading: false, storageError: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        storageError:
          "Le stockage local (IndexedDB) n'est pas accessible dans ce navigateur ou cet onglet (navigation privée ?). Tes parties et exercices ne pourront pas être sauvegardés.",
      }));
      void err;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
